import { useState } from "react";
import { Car, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getPosition, TYPE_LABEL } from "@/lib/directions";
import { TrailheadFacilities } from "@/components/rider/app/TrailheadFacilities";

// Draws a driving route to the nearest trailhead on the Velopaths map (never leaves the app).
export function DriveToTrailhead({ pathId, route, onRoute }) {
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(null);

  const load = async (index = 0, here = pos) => {
    setBusy(true);
    try {
      const p = here || await getPosition();
      if (!p) { toast.error("Turn on location so we can show the drive from where you are."); return; }
      setPos(p);
      const { data } = await api.get(`/rider/bike-paths/${encodeURIComponent(pathId)}/drive-route`, { params: { lat: p[0], lng: p[1], trailhead: index } });
      onRoute(data);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "No trailhead is available for this path yet." : "Couldn't load the route. Please try again.");
    } finally { setBusy(false); }
  };

  if (!route) {
    return (
      <button onClick={() => load(0)} disabled={busy} data-testid="drive-route-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-5 py-3 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />}
        {busy ? "Finding the route…" : "Show drive to trailhead"}
      </button>
    );
  }
  const r = route.route, th = route.trailhead;
  return (
    <div className="rounded-2xl border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="drive-route-summary">
      <div className="flex items-start gap-2">
        <Car className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="drive-route-trailhead">{th.name}</div>
          <div className="text-xs text-muted-foreground" data-testid="drive-route-stats">
            {r.distance_mi} mi{r.duration_min ? ` · about ${r.duration_min} min drive` : " in a straight line"}
          </div>
        </div>
        <button onClick={() => onRoute(null)} aria-label="Clear route" data-testid="drive-route-clear" className="p-1 rounded-full hover:bg-secondary transition-colors"><X className="w-4 h-4" /></button>
      </div>
      {route.access_points.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="drive-route-options">
          {route.access_points.map((p, i) => (
            <button key={i} onClick={() => load(i)} disabled={busy || i === th.index} data-testid={`drive-route-option-${i}`}
              className={`text-[11px] rounded-full border px-2.5 py-1 transition-colors ${i === th.index ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
              {p.name}{p.distance_mi != null ? ` · ${p.distance_mi} mi` : ""}<span className="sr-only"> ({TYPE_LABEL[p.type] || p.type})</span>
            </button>
          ))}
        </div>
      )}
      <TrailheadFacilities facilities={route.facilities} />
      {route.attribution && <p className="text-[10px] text-muted-foreground">{route.attribution}</p>}
    </div>
  );
}
